/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable consistent-return */
/* eslint-disable no-useless-concat */
/* eslint-disable no-param-reassign */
/* eslint-disable react/no-unused-prop-types */
import fs from "fs";

import React, { useContext, useEffect } from "react";
import { useRouter } from "next/router";
import Head from "next/head";

import { DocChrome } from "@chrome/DocChrome";
import { MenuItem, PageType } from "@components/layout/Menu";
import LandingPages, { determineMenuType } from "@config/landing-pages";
import Doc from "@components/Doc";
import { LandingPage } from "@components/LandingPage";

import { LayoutContext } from "@components/layout/Layout";

import { DocMeta, Options } from "@content/en/Options";
import {
  stringToURI,
  getTextAfterChar,
  pathMatchesSlugs,
  prettyStr,
  formatOptionVal,
  sortFileNamesByNumber,
} from "@scripts/utils";
import { DocConfig, MainMenuData } from "@config/nav";
import { SiteHtmlPageTitle } from "@content/en/HtmlTitle";
import {
  documents,
  galaxiesExcludedFromSlugProcessing,
} from "@content/en/Documents";

type PageProps = {
  galaxy: string;
  menuData: MenuItem[];
  docFilePath: string;
  docConfig: DocConfig;
  docProps: DocProps;
};

// TODO: replace DocPage and LandingPage with 1 index component
const DocPage = ({ galaxy, docFilePath, docConfig, docProps }: PageProps) => {
  // const { lang } = useTranslation() // locale is what this should be called eventually

  const { REPLCollapsed, setREPLCollapsed } = useContext(LayoutContext);
  const router = useRouter();
  const { query } = router;

  // get document name from last token in path (filename)
  const docSlugName = stringToURI(
    getTextAfterChar(docFilePath.split("/").reverse()[0], " "),
  );
  const Document = documents[docSlugName];

  // called when galaxy or url slugs change
  useEffect(() => {
    // open repl for quickstart by default
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    query.galaxy === "quickstart" &&
      (docSlugName === "notecard-quickstart" ||
        docSlugName === "notecard-lora-quickstart") &&
      REPLCollapsed &&
      setREPLCollapsed(false);
  }, [query.slugs, query.galaxy]);

  const galaxyItem = MainMenuData.items.find(
    (item) => item.href === `/${galaxy}`,
  );

  return (
    <Doc
      galaxyTitle={galaxyItem?.title || ""}
      galaxyURL={`/${galaxy}`}
      pageTitle={docConfig.navTitle}
      pageURL={docConfig.pathBase}
    >
      {React.cloneElement(<Document />, {
        ...docProps,
        urlConfig: docConfig,
        allOptions: Options,
      })}
    </Doc>
  );
};

const Index = (props: PageProps) => {
  const { galaxy, menuData, docFilePath, docConfig } = props;
  const { query } = useRouter();
  const isDocPage = !!docFilePath;
  const landingPageName = `${galaxy}${
    query.slugs && Array.isArray(query.slugs) ? `/${query.slugs.join("/")}` : ""
  }`;

  let title = "";
  let description = "";
  let leftMenuType: PageType | undefined;

  if (isDocPage) {
    title = `${docConfig.navTitle} - ${SiteHtmlPageTitle}`;
    description = docConfig.description;
    leftMenuType = "galaxy";
  } else {
    title = `${prettyStr(
      query.slugs ? query.slugs.slice(-1)[0] : landingPageName,
    )} - ${SiteHtmlPageTitle}`;
    description = LandingPages[galaxy]?.description;
    leftMenuType = determineMenuType(galaxy);
  }

  return (
    <>
      <Head>
        <meta name="description" content={description} />
      </Head>
      <DocChrome
        selectedPage={galaxy}
        leftMenu={menuData}
        leftMenuType={leftMenuType}
        title={title}
      >
        {isDocPage ? (
          <DocPage {...props} />
        ) : (
          <LandingPage name={landingPageName} />
        )}
      </DocChrome>
    </>
  );
};

type DocProps = {
  [key: string]: string;
};

export async function getStaticProps(context: any) {
  const p = context.params;
  let filePath = "";
  let propsSet = false;
  const docProps: DocProps = {};
  let docConfig: DocConfig = {
    allPaths: [],
    defaultSlugVals: [],
    description: "",
    keywords: [],
    navTitle: "",
    pathBase: "/",
    searchOnlyDefaultSlugs: false,
    slugs: [],
  };
  const menuData: MenuItem[] = [];
  const { galaxy } = p;
  // TODO change en to ${lang}
  const contentPath = `content/en/${galaxy}`;

  // returns an array of folder names for a given path
  // ex) path '/content/en/quickstart/02 Tutorials' would return ['01 Sensor Tutorial','02 Route Tutorial']
  const getDirectories = (source: string) =>
    fs
      .readdirSync(source, { withFileTypes: true })
      .filter((dirent) => dirent.isDirectory())
      .map((dirent) => dirent.name);

  // returns an array of all file and folder names in a given path
  // {format} returns the file names without the number prefix and
  // .mdx extension (mdx document section titles)
  const getFileNames = (sourcePath: string, format?: boolean) => {
    // all files with that don't begin with an underscore, has an mdx extension or is a folder
    const dirNames = fs
      .readdirSync(sourcePath, { withFileTypes: true })
      .filter(
        (dirent) =>
          dirent.name.match(/^(?=[^_])(?=.*\.mdx$)/) || dirent.isDirectory(),
      );

    dirNames.sort((a, b) => sortFileNamesByNumber(a.name, b.name));

    return format
      ? // remove number prefix and extension
        dirNames.map((dirent) => dirent.name.replace(/(^\d{2,3} |.mdx$)/g, ""))
      : dirNames.map((folder) => folder.name);
  };

  // Create menu data and set page props
  const BuildMenu = (
    folder: string,
    path: string,
    menu: MenuItem,
    meta?: DocMeta,
  ) => {
    menu.level += 1;
    menu.title = getTextAfterChar(folder, " ");

    // selectable menu items (current page) will have slugs and match the path
    if (p.slugs && pathMatchesSlugs(galaxy, `${path}/${folder}`, p.slugs)) {
      if (menu.type === "doc") {
        menu.selected = true;
      }
      // selected folders will be the last slug in array
      else if (
        menu.type === "folder" &&
        p.slugs.slice(-1)[0] === stringToURI(menu.title)
      ) {
        menu.selected = true;
      }
    }

    // build url from filepath string
    // ex) content/en/start/01 Tutorials/01 Sensor Tutorial => /start/tutorials/sensor-tutorial
    menu.href = `/${path
      .split("/")
      .map((name) => stringToURI(getTextAfterChar(name, " ")))
      .slice(path.split("/").indexOf(galaxy))
      .join("/")}/${stringToURI(menu.title)}`;

    menu.docConfig = {
      allPaths: meta?.allPaths || [],
      defaultSlugVals: meta?.defaultVals || [],
      description: meta?.description || "",
      keywords: meta?.keywords || [],
      navTitle: meta?.navTitle || "",
      pathBase: menu.href || "",
      slugs: meta?.props || [],
      searchOnlyDefaultSlugs: meta?.searchOnlyDefaultSlugs || false,
    };

    menu.items =
      menu.type === "doc"
        ? // all file/folders names in a doc folder are section titles to be added to the menu
          getFileNames(`${path}/${folder}`, true).map((folderName) => ({
            level: menu.level + 1,
            selected: false,
            title: folderName,
            type: "section",
            href: "",
            items: [],
          }))
        : getFileNames(`${path}/${folder}`, false)
            .sort(sortFileNamesByNumber)
            // filter unpublished docs on staging/products
            .filter((file) => {
              const folderPath = `${path}/${folder}/${file}`;

              if (fs.existsSync(`${folderPath}/_meta.json`)) {
                const { publicationStatus } = JSON.parse(
                  fs.readFileSync(`${folderPath}/_meta.json`, "utf8"),
                );
                if (
                  publicationStatus === "unpublished" &&
                  process.env.NEXT_PUBLIC_ENV_NAME !== "development"
                ) {
                  return;
                }
              }
              return true;
              // get type by checking if _meta.json present and build menu data for it
            })
            .map((file) => {
              let docMeta: DocMeta | undefined;
              const newMenu: MenuItem = { ...menu };
              const folderPath = `${path}/${folder}/${file}`;

              newMenu.type = fs.existsSync(`${folderPath}/_meta.json`)
                ? "doc"
                : "folder";

              newMenu.selected = false;

              if (newMenu.type === "doc") {
                docMeta = JSON.parse(
                  fs.readFileSync(`${folderPath}/_meta.json`, "utf8"),
                );
              }

              return BuildMenu(
                file,
                `${path}/${folder}`,
                newMenu,
                docMeta,
              ) as MenuItem;
            });

    // sets page props
    function setPageProps() {
      filePath = menu.type === "doc" ? `${path}/${folder}` : "";

      docConfig = menu.docConfig ? menu.docConfig : docConfig;

      // array of doc props from url slugs starting with doc name
      const docSlugs = p.slugs.slice(
        p.slugs.indexOf(stringToURI(menu.title)) + 1,
      );

      // set props using url slugs if count matches doc props or use doc defaults
      if (
        p.slugs &&
        menu.docConfig?.slugs &&
        docSlugs.length === menu.docConfig.slugs.length
      ) {
        docSlugs.forEach((val: string, idx: number) => {
          if (menu.docConfig?.slugs) {
            docProps[menu.docConfig.slugs[idx]] = val;
          }
        });
      } else {
        meta?.defaultVals.forEach((defaultVal, idx) => {
          docProps[meta?.props[idx]] = defaultVal;
        });
      }

      // for routes with dynamic inputs, generate all the possible combinations server side to account for AddSearch crawling and SEO
      if (docConfig.navTitle.includes("Notecard Datasheet")) {
        const formattedNotecardName = formatOptionVal(
          Options,
          "notecard",
          docProps.notecard,
        );
        docConfig.navTitle = `Notecard Datasheet - ${formattedNotecardName}`;
      } else if (docConfig.navTitle.includes("Notecarrier Datasheet")) {
        const formattedNotecarrierName = formatOptionVal(
          Options,
          "notecarrier",
          docProps.notecarrier,
        );
        docConfig.navTitle = `Notecarrier Datasheet - ${formattedNotecarrierName}`;
      } else if (docConfig.navTitle.includes("Route Tutorial")) {
        const formattedRouteTutorialName = formatOptionVal(
          Options,
          "service",
          docProps.service,
        );
        docConfig.navTitle = `Route Tutorial - ${formattedRouteTutorialName}`;
      } else if (docConfig.navTitle.includes("Sensor Tutorial")) {
        const formattedLanguageOption = formatOptionVal(
          Options,
          "language",
          docProps.language,
        );
        const formattedBoardOption = formatOptionVal(
          Options,
          "board",
          docProps.board,
        );
        const formattedNotecarrierOption = formatOptionVal(
          Options,
          "notecarrier",
          docProps.notecarrier,
        );
        docConfig.navTitle = `Sensor Tutorial with ${formattedLanguageOption}, ${formattedBoardOption}, and ${formattedNotecarrierOption}`;
      }

      // set flag
      propsSet = true;
    }

    // set document/component props if the path url slugs match param url slugs
    // ex) 'content/en/start/tutorials/01 Sensor Tutorial' matches slug array ['tutorials','sensor-tutorial']
    if (!propsSet && p.slugs && menu.level > 0) {
      // convert path to array and slice slug subset
      if (pathMatchesSlugs(galaxy, `${path}/${folder}`, p.slugs)) {
        setPageProps();
      }
    }

    return menu;
  };

  // core function to iterate through a content path to build menu data
  // root is either a path to a galaxy directory ex) `content/en/start`
  // or all galaxies ex) `content/en`
  getDirectories(contentPath).forEach((folder) => {
    const menu: MenuItem = {
      level: 0,
      selected: false,
      type: "folder",
      title: "",
      items: [],
    };

    let meta: DocMeta | undefined;
    if (fs.existsSync(`${contentPath}/${folder}/_meta.json`)) {
      menu.type = "doc";
      meta = JSON.parse(
        fs.readFileSync(`${contentPath}/${folder}/_meta.json`, "utf8"),
      );

      // skip unpublished docs on staging/production
      if (
        meta?.publicationStatus === "unpublished" &&
        process.env.NEXT_PUBLIC_ENV_NAME !== "development"
      ) {
        return;
      }
    }

    menuData.push(BuildMenu(folder, contentPath, menu, meta));
  });

  return {
    props: {
      galaxy,
      menuData,
      docFilePath: filePath,
      docConfig,
      docProps,
    },
  };
}

interface ParamProps {
  galaxy: string;
  slugs: string[];
}

interface Params {
  params: ParamProps;
}

// create url paths for static generation
export async function getStaticPaths() {
  const allPaths: Array<Params> = [];

  const getDirectories = (source: string) =>
    fs
      .readdirSync(source, { withFileTypes: true })
      .filter((dirent) => dirent.isDirectory())
      .map((dirent) => dirent.name);

  // creates params for page and return whether the path is a document folder
  const addPath = (dirPath: string, slugPath: string, galaxy: string) => {
    const slugs = (slugPath.split("/") || [slugPath]).map((val) =>
      stringToURI(getTextAfterChar(val, " ")),
    );

    // add path
    allPaths.push({
      params: {
        galaxy,
        slugs,
      },
    });

    // folder is a document if _meta.json exists
    // add path, and if doc is published, add url
    if (fs.existsSync(`${dirPath}/_meta.json`)) {
      const config = JSON.parse(
        fs.readFileSync(`${dirPath}/_meta.json`, "utf8"),
      ) as DocMeta;

      // add prop combinations for document
      config.allPaths.forEach((str) => {
        allPaths.push({
          params: {
            galaxy,
            slugs: slugs.concat(str.split("/")),
          },
        });
      });
    }
    // path is a folder, iterate through subfolders
    else {
      processFolder(dirPath, slugPath, galaxy);
    }
  };

  const processFolder = (
    path: string,
    pageSlugPath: string,
    galaxy: string,
  ) => {
    getDirectories(path).forEach((folder) => {
      addPath(
        `${path}/${folder}`,
        `${pageSlugPath ? `${pageSlugPath}/${folder}` : folder}`,
        galaxy,
      );
    });
  };

  // starting function to generate paths
  const init = () => {
    // loop through galaxy directories
    getDirectories(`content/en`).forEach((folder) => {
      const galaxy = stringToURI(folder);
      if (!galaxiesExcludedFromSlugProcessing.includes(galaxy)) {
        // add root galaxy url to paths
        allPaths.push({
          params: {
            galaxy,
            slugs: [],
          },
        });

        // iterate through subfolders
        processFolder(`content/en/${folder}`, "", galaxy);
      }
    });
  };

  // generate paths
  init();

  return {
    paths: allPaths,
    fallback: false,
  };
}

export default Index;
